import './PuxNumber.scss'

import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { getIsoCodesWithPrefixPublic } from 'Shared/utils/localeURL'

import SemiCircle from '../helper/SemiCircle'

export interface PuxNumberType {
  numberDescription: string
  numberPrefix: string
  numberUnit: string
  numberValue: number
  numberShowProgress: boolean
  contentItemId: string
  numberRadius: number
  numberStroke: number
}

const ANIMATION_DURATION = 1500

const PuxNumber: FunctionComponent<PuxNumberType> = (props) => {
  const project: string = process.env.PROJECT
    ? process.env.PROJECT
    : `EasyRedmineGatsby`

  const isoCode =
    getIsoCodesWithPrefixPublic(false, project).filter(
      (iso) => iso.locale === process.env.LOCALE
    )[0].isoCode ?? `en-GB`

  const prefix = `${props.numberPrefix ? props.numberPrefix + ` ` : ``}`
  const progressPrefix = `${props.numberPrefix ? props.numberPrefix : ``}`
  const unit = `${props.numberUnit ? props.numberUnit : ``}`
  const formatedNumber =
    prefix + new Intl.NumberFormat(isoCode).format(props.numberValue) + unit

  const [hasMounted, setHasMounted] = useState(false)
  const [isAnimating, setIsAnimating] = useState(false)
  const [animatedValue, setAnimatedValue] = useState(0)
  const [animatedProgress, setAnimatedProgress] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const animationFrameRef = useRef<number>(0)
  const hasTriggeredRef = useRef(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  const startAnimation = useCallback(() => {
    if (hasTriggeredRef.current) return
    hasTriggeredRef.current = true
    setIsAnimating(true)

    requestAnimationFrame(() => {
      setAnimatedProgress(props.numberValue)
    })

    const startTime = performance.now()
    const targetValue = props.numberValue

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const rawProgress = Math.min(elapsed / ANIMATION_DURATION, 1)
      const easedProgress = 1 - Math.pow(1 - rawProgress, 3)

      setAnimatedValue(Math.round(easedProgress * targetValue))

      if (rawProgress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate)
      } else {
        setAnimatedValue(targetValue)
      }
    }

    animationFrameRef.current = requestAnimationFrame(animate)
  }, [props.numberValue])

  useEffect(() => {
    if (!props.numberShowProgress || typeof window === `undefined`) return

    const element = containerRef.current
    if (!element) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startAnimation()
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.3 }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [props.numberShowProgress, startAnimation])

  const getProgressValue = (): string => {
    if (!hasMounted)
      return new Intl.NumberFormat(isoCode).format(props.numberValue)
    if (isAnimating) return new Intl.NumberFormat(isoCode).format(animatedValue)
    return new Intl.NumberFormat(isoCode).format(0)
  }

  const getArcProgress = (): number => {
    if (!hasMounted) return props.numberValue
    return animatedProgress
  }

  return (
    <div
      className={`Number${props.numberShowProgress ? ` Number--progress` : ``}`}
      ref={containerRef}
    >
      {props.numberShowProgress && (
        <div className='Number-progress'>
          <SemiCircle
            customClass='Number-progressBackground'
            progress={100}
            radius={props.numberRadius}
            stroke={props.numberStroke}
          />
          <SemiCircle
            customClass='Number-progressMain'
            progress={getArcProgress()}
            radius={props.numberRadius}
            stroke={props.numberStroke}
            animated={isAnimating}
          />
          <p className='Number-progressValue'>
            <span>
              {progressPrefix}
              {getProgressValue()}
            </span>
            {unit && <span className='Number-progressUnit'>{unit}</span>}
          </p>
        </div>
      )}
      {!props.numberShowProgress && (
        <p className='Number-value'>{formatedNumber}</p>
      )}
      <p className='Number-description'>
        {props.numberDescription ? props.numberDescription : ``}
      </p>
    </div>
  )
}

export default PuxNumber
